const resolvedCache = new Map<string, string>(); // url → url (same-origin only)
const pendingCache = new Map<string, Promise<string>>();
const cacheTimestamps = new Map<string, number>(); // url → insertion timestamp (ms)

const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function isSameOrigin(url: string): boolean {
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
}

function isExpired(url: string): boolean {
  const ts = cacheTimestamps.get(url);
  return ts !== undefined && Date.now() - ts > TTL_MS;
}

function evictEntry(url: string): void {
  const blobUrl = resolvedCache.get(url);
  if (blobUrl && blobUrl !== url) URL.revokeObjectURL(blobUrl);
  resolvedCache.delete(url);
  cacheTimestamps.delete(url);
}

function setEntry(url: string, value: string): void {
  if (resolvedCache.size >= 200) {
    const oldestKey = resolvedCache.keys().next().value;
    evictEntry(oldestKey as string);
  }
  resolvedCache.set(url, value);
  cacheTimestamps.set(url, Date.now());
}

export function getResolvedCachedUrl(url: string): string | undefined {
  if (isExpired(url)) {
    evictEntry(url);
    return undefined;
  }
  return resolvedCache.get(url);
}

export function clearImageCache(): void {
  resolvedCache.forEach((value, key) => {
    if (value !== key) URL.revokeObjectURL(value);
  });
  resolvedCache.clear();
  cacheTimestamps.clear();
  pendingCache.clear();
}

export function getCachedImageUrl(url: string): Promise<string> {
  if (!url) return Promise.resolve(url);

  // For cross-origin URLs, skip fetch — return as-is (browser caches via ETag/Last-Modified)
  if (!isSameOrigin(url)) {
    setEntry(url, url);
    return Promise.resolve(url);
  }

  if (resolvedCache.has(url) && !isExpired(url))
    return Promise.resolve(resolvedCache.get(url)!);
  if (isExpired(url)) evictEntry(url);
  if (pendingCache.has(url)) return pendingCache.get(url)!;

  const promise = fetch(url)
    .then((r) => r.blob())
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      setEntry(url, blobUrl);
      pendingCache.delete(url);
      return blobUrl;
    })
    .catch(() => {
      pendingCache.delete(url);
      return url;
    });

  pendingCache.set(url, promise);
  return promise;
}
